import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { clientApi } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency } from "@/lib/format";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { ArrowLeft, TrendingUp, Repeat, Clock, Landmark, Star, GitCompare } from "lucide-react";
import { toast } from "sonner";

const RANGES = ["1m", "6m", "1y", "3y", "5y", "max"];

function NavChart({ schemeCode }) {
  const [range, setRange] = useState("1y");
  const [hist, setHist] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    clientApi.get(`/schemes/${schemeCode}/nav-history?range=${range}`).then((r) => setHist(r.data)).finally(() => setLoading(false));
  }, [schemeCode, range]);

  const points = hist?.points || [];
  const first = points[0]?.nav, last = points[points.length - 1]?.nav;
  const changePct = first && last ? ((last / first - 1) * 100).toFixed(2) : null;
  const up = (changePct ?? 0) >= 0;

  return (
    <Card className="glass-card mt-6 border-0">
      <CardContent className="p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-[var(--brand)]">NAV history (real AMFI data)</div>
            {changePct != null && <div className={`text-xs font-medium ${up ? "text-emerald-700" : "text-red-600"}`} data-testid="nav-chart-change">{up ? "+" : ""}{changePct}% in {range}</div>}
          </div>
          <div className="flex gap-1">
            {RANGES.map((r) => (
              <button key={r} onClick={() => setRange(r)} data-testid={`nav-range-${r}`}
                className={`rounded-full px-3 py-1 text-xs font-medium ${range === r ? "bg-[var(--brand)] text-white" : "text-[var(--ink-muted)] hover:bg-[var(--surface-2)]"}`}>
                {r.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        {loading ? <Skeleton className="h-56 rounded-xl" /> : points.length === 0 ? (
          <div className="flex h-56 items-center justify-center text-sm text-[var(--ink-muted)]">NAV history unavailable for this plan.</div>
        ) : (
          <div className="h-56" data-testid="nav-chart">
            <ResponsiveContainer width="100%" height={224} minWidth={0}>
              <AreaChart data={points}>
                <defs>
                  <linearGradient id="navGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={up ? "#0a9970" : "#dc2626"} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={up ? "#0a9970" : "#dc2626"} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={60} />
                <YAxis domain={["auto", "auto"]} tick={{ fontSize: 10 }} width={55} />
                <Tooltip formatter={(v) => [`₹${v}`, "NAV"]} labelStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="nav" stroke={up ? "#0a9970" : "#dc2626"} strokeWidth={1.8} fill="url(#navGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        {hist?.trailing_returns && Object.keys(hist.trailing_returns).length > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-6">
            {Object.entries(hist.trailing_returns).map(([p, v]) => (
              <div key={p} className="rounded-xl bg-[var(--surface-2)] p-2 text-center" data-testid={`trailing-${p}`}>
                <div className="text-[10px] uppercase text-[var(--ink-muted)]">{p}{["1y","3y","5y"].includes(p) ? " CAGR" : ""}</div>
                <div className={`text-sm font-semibold ${v >= 0 ? "text-emerald-700" : "text-red-600"}`}>{v > 0 ? "+" : ""}{v}%</div>
              </div>
            ))}
          </div>
        )}
        {hist?.inception_date && <div className="mt-3 text-xs text-[var(--ink-muted)]">Inception: {hist.inception_date} · Source: AMFI / MFapi</div>}
      </CardContent>
    </Card>
  );
}

function FundAnalytics({ schemeCode }) {
  const [an, setAn] = useState(null);
  const [peers, setPeers] = useState(null);

  useEffect(() => {
    clientApi.get(`/schemes/${schemeCode}/analytics`).then((r) => setAn(r.data)).catch(() => {});
    clientApi.get(`/schemes/${schemeCode}/peers`).then((r) => setPeers(r.data)).catch(() => {});
  }, [schemeCode]);

  const risk = an?.risk || {};
  const riskChips = [
    ["Volatility (SD)", risk.sd_annualized != null ? `${risk.sd_annualized}%` : "—"],
    ["Sharpe", risk.sharpe ?? "—"],
    ["Beta", risk.beta ?? "—"],
    ["Max Drawdown", risk.max_drawdown != null ? `${risk.max_drawdown}%` : "—"],
    ["3Y CAGR", risk.cagr_3y != null ? `${risk.cagr_3y}%` : "—"],
  ];

  return (
    <>
      {an && Object.keys(risk).length > 0 && (
        <Card className="glass-card mt-6 border-0">
          <CardContent className="p-5">
            <div className="mb-3 text-sm font-medium text-[var(--brand)]">Risk & Rolling Returns <span className="text-xs font-normal text-[var(--ink-muted)]">(3Y daily · benchmark: {an.benchmark?.slice(0, 40)})</span></div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="risk-metrics">
              {riskChips.map(([l, v]) => (
                <div key={l} className="rounded-xl bg-[var(--surface-2)] p-2.5 text-center">
                  <div className="text-[10px] uppercase text-[var(--ink-muted)]">{l}</div>
                  <div className="text-sm font-semibold text-[var(--brand)]">{v}</div>
                </div>
              ))}
            </div>
            {an.rolling && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-xs" data-testid="rolling-returns-table">
                  <thead><tr className="border-b border-[var(--glass-border)] text-left text-[var(--ink-muted)]">
                    <th className="p-2 font-medium">Rolling window</th><th className="p-2 text-right font-medium">Avg</th><th className="p-2 text-right font-medium">Median</th><th className="p-2 text-right font-medium">Min</th><th className="p-2 text-right font-medium">Max</th><th className="p-2 text-right font-medium">% times positive</th><th className="p-2 text-right font-medium">% times &gt;8%</th>
                  </tr></thead>
                  <tbody className="divide-y divide-[var(--glass-border)]">
                    {Object.entries(an.rolling).filter(([, v]) => v && v.observations).map(([w, v]) => (
                      <tr key={w}>
                        <td className="p-2 font-medium uppercase">{w} ({v.observations} obs)</td>
                        <td className="p-2 text-right font-semibold text-emerald-700">{v.avg}%</td>
                        <td className="p-2 text-right">{v.median}%</td>
                        <td className="p-2 text-right text-red-600">{v.min}%</td>
                        <td className="p-2 text-right text-emerald-700">{v.max}%</td>
                        <td className="p-2 text-right">{v.positive_pct}%</td>
                        <td className="p-2 text-right">{v.above_8_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {peers?.peers?.length > 1 && (
        <Card className="glass-card mt-6 border-0">
          <CardContent className="p-5">
            <div className="mb-3 text-sm font-medium text-[var(--brand)]">Peer comparison — {peers.sub_category}</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="peers-table">
                <thead><tr className="border-b border-[var(--glass-border)] text-left text-[var(--ink-muted)]">
                  <th className="p-2 font-medium">Fund</th><th className="p-2 text-right font-medium">NAV</th><th className="p-2 text-right font-medium">1Y</th><th className="p-2 text-right font-medium">3Y CAGR</th><th className="p-2 text-right font-medium">5Y CAGR</th>
                </tr></thead>
                <tbody className="divide-y divide-[var(--glass-border)]">
                  {peers.peers.map((p) => (
                    <tr key={p.scheme_code} className={p.is_self ? "bg-[var(--surface-2)]" : ""}>
                      <td className="max-w-[280px] truncate p-2 font-medium">{p.is_self ? "★ " : ""}{p.scheme_name}</td>
                      <td className="p-2 text-right">₹{p.nav?.toFixed(2)}</td>
                      {["1y", "3y", "5y"].map((k) => {
                        const v = p.trailing_returns?.[k];
                        return <td key={k} className={`p-2 text-right ${v >= 0 ? "text-emerald-700" : "text-red-600"}`}>{v != null ? `${v > 0 ? "+" : ""}${v}%` : "—"}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

export default function FundDetail() {
  const { schemeCode } = useParams();
  const navigate = useNavigate();
  const [scheme, setScheme] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    clientApi.get(`/schemes/${schemeCode}`).then((r) => setScheme(r.data)).finally(() => setLoading(false));
  }, [schemeCode]);

  const addToWatchlist = async () => {
    try {
      await clientApi.post("/watchlist", { scheme_code: schemeCode });
      toast.success("Added to your watchlist");
    } catch {
      toast.error("Could not add to watchlist");
    }
  };

  if (loading) {
    return (
      <ClientLayout>
        <Skeleton className="h-64 rounded-2xl" />
      </ClientLayout>
    );
  }

  if (!scheme) {
    return (
      <ClientLayout>
        <div className="py-16 text-center text-[var(--ink-muted)]">Scheme not found.</div>
      </ClientLayout>
    );
  }

  const transactionTerms = scheme.transaction_metadata_verified;
  const facts = [
    { label: "Latest NAV", value: `₹${scheme.nav?.toFixed(4)} (${scheme.nav_date})` },
    { label: "Category", value: `${scheme.sub_category || scheme.scheme_type}` },
    { label: "Riskometer", value: scheme.riskometer || "—" },
    { label: "Minimum Purchase", value: transactionTerms ? formatCurrency(scheme.min_purchase_amt) : "Confirmed at order time" },
    { label: "Additional Purchase", value: transactionTerms ? formatCurrency(scheme.additional_purchase_amt) : "As per AMC/BSE" },
    { label: "Purchase Cutoff", value: transactionTerms ? scheme.purchase_cutoff_time : "As per AMC/BSE" },
    { label: "Settlement", value: transactionTerms ? scheme.settlement_type : "As per AMC/BSE" },
    { label: "Exit Load", value: transactionTerms ? (scheme.exit_load_flag ? `${scheme.exit_load}%` : "Nil") : "As per AMC/BSE" },
  ];

  return (
    <ClientLayout>
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1 text-sm text-[var(--ink-muted)]" data-testid="fund-detail-back-button">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="glass-card mb-6 p-6">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--brand)]">
              <Landmark className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-xl text-[var(--brand)] sm:text-2xl" data-testid="fund-detail-name">{scheme.scheme_name}</h1>
              <div className="mt-1 flex flex-wrap gap-2 text-xs text-[var(--ink-muted)]">
                <span>{scheme.amc_name}</span> &middot; <span>{scheme.scheme_type}</span> &middot; <span>ISIN {scheme.isin}</span>
              </div>
              <div className="mt-2 flex gap-1.5">
                <Badge variant="outline" className="rounded-full">Regular Plan</Badge>
                <Badge variant="outline" className="rounded-full">{scheme.plan_variant}</Badge>
                {scheme.sip_flag === true && <Badge className="rounded-full bg-[var(--surface-2)] text-[var(--brand)]">SIP Eligible</Badge>}
                {!transactionTerms && <Badge variant="outline" className="rounded-full">Terms checked by BSE</Badge>}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {scheme.purchase_allowed !== false && (
              <Button asChild className="rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="fund-detail-invest-button">
                <Link to={`/invest/${scheme.scheme_code}`}><TrendingUp className="mr-2 h-4 w-4" />Invest Now</Link>
              </Button>
            )}
            {scheme.sip_flag !== false && (
              <Button asChild variant="outline" className="rounded-full" data-testid="fund-detail-sip-button">
                <Link to={`/sips/new?scheme=${scheme.scheme_code}`}><Repeat className="mr-2 h-4 w-4" />Start SIP</Link>
              </Button>
            )}
            <Button variant="outline" className="rounded-full" onClick={addToWatchlist} data-testid="fund-detail-watch-button">
              <Star className="mr-1 h-4 w-4" /> Watch
            </Button>
            <Button asChild variant="outline" className="rounded-full" data-testid="fund-detail-compare-button">
              <Link to={`/compare?codes=${scheme.scheme_code}`}><GitCompare className="mr-1 h-4 w-4" /> Compare</Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {facts.map((f) => (
          <Card key={f.label} className="border-[var(--glass-border)]">
            <CardContent className="p-4">
              <div className="text-xs text-[var(--ink-muted)]">{f.label}</div>
              <div className="mt-1 font-semibold text-[var(--brand)]">{f.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <NavChart schemeCode={scheme.scheme_code} />

      <FundAnalytics schemeCode={scheme.scheme_code} />

      {scheme.variants?.length > 1 && (
        <div className="mt-8">
          <h2 className="mb-3 font-heading text-lg text-[var(--brand)]">Other plan variants</h2>
          <div className="flex flex-wrap gap-2">
            {scheme.variants.map((v) => (
              <Link
                key={v.scheme_code}
                to={`/funds/${v.scheme_code}`}
                data-testid={`variant-link-${v.scheme_code}`}
                className={`rounded-full border px-3 py-1.5 text-xs ${v.scheme_code === scheme.scheme_code ? "border-[var(--cta)] bg-[var(--surface-2)] text-[var(--brand)] font-medium" : "border-[var(--glass-border)] text-[var(--ink-muted)] hover:bg-[var(--surface-2)]"}`}
              >
                {v.plan_variant} &middot; Regular
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 flex items-center gap-2 text-xs text-[var(--ink-muted)]">
        <Clock className="h-3.5 w-3.5" /> Orders are processed via BSE StAR MF; eligibility, limits, cut-off, settlement and exit load are confirmed by BSE/AMC.
      </div>
    </ClientLayout>
  );
}
